import { useLink } from "@grandvisionhq/frk-routing";
import { useIntl } from "@grandvisionhq/state";
import { logMyOrdersClickEvent } from "../../data-layer/data-layer/my-orders-click.js";
import { getParsedDataFromLS } from "../../Util/Util/customer-name-persistor.js";
function getActivePath() {
  if (typeof window === "undefined") {
    return "";
  }
  const url = new URL(window.location.href);
  return url.pathname;
}
function hasRoute(route) {
  return Boolean(route);
}
const useMyAccountMenuItems = ({
  logout,
  routes,
  position,
  isUserLoggedIn,
  isAabEnabled,
  isWishlistEnabled
}) => {
  const intl = useIntl();
  const activePath = getActivePath();
  const loginLinkAttributes = useLink({ route: "auth.login" });
  const profileLinkAttributes = useLink({ route: "me.profile" });
  const prescriptionsLinkAttributes = useLink({ route: "me.prescription" });
  const ordersLinkAttributes = useLink({ route: "me.orders" });
  const appointmentsLinkAttributes = useLink({ route: "me.appointments" });
  const cccSubscriptionsLinkAttributes = useLink({ route: "me.cccSubscriptions" });
  const subscriptionsLinkAttributes = useLink({ route: "me.subscriptions" });
  const settingsLinkAttributes = useLink({ route: "me.settings" });
  const wishlistLinkAttributes = useLink({ route: "me.wishlist" });
  const getLinkAttributes = (linkProps, {
    highlightOnUrlPath,
    isLinkEnabled,
    overrideLinkAttributes,
    skipHighlight = false
  } = {}) => {
    if (!isLinkEnabled) {
      return void 0;
    }
    const active = activePath.startsWith(highlightOnUrlPath ?? linkProps?.href ?? "unknown");
    return {
      type: "external",
      ...!skipHighlight ? { active } : null,
      href: linkProps?.href ?? "",
      // empty string links will be filtered out at the end of the root function
      onClick: linkProps?.onClick ?? void 0,
      ...overrideLinkAttributes
    };
  };
  const logoutItem = {
    label: intl.getLabel("account.menu.logOut"),
    parameters: {
      icon: "logout",
      action: logout,
      testId: "logout"
    },
    link: { href: "", type: "external" }
  };
  const loginItem = {
    label: intl.getLabel("account.action.login"),
    parameters: {
      icon: "login",
      testId: "login"
    },
    link: getLinkAttributes(loginLinkAttributes, {
      isLinkEnabled: hasRoute(routes?.profile),
      skipHighlight: true
    })
  };
  const userHasParsedDataFromLS = getParsedDataFromLS();
  let lastItem = logoutItem;
  if (!isUserLoggedIn && !userHasParsedDataFromLS) {
    lastItem = loginItem;
  }
  const items = [
    {
      label: intl.getLabel("account.menu.accountDetails"),
      parameters: {
        icon: "user",
        testId: "profile"
      },
      link: getLinkAttributes(profileLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.profile)
      })
    },
    {
      label: intl.getLabel("account.menu.appointments"),
      parameters: {
        icon: "calendar",
        testId: "appointments"
      },
      link: getLinkAttributes(appointmentsLinkAttributes, {
        isLinkEnabled: isAabEnabled && hasRoute(routes?.appointments)
      })
    },
    {
      label: intl.getLabel("account.menu.prescriptions"),
      parameters: {
        icon: "frame",
        testId: "prescriptions"
      },
      link: getLinkAttributes(prescriptionsLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.prescriptions)
      })
    },
    {
      label: intl.getLabel("account.menu.orders"),
      parameters: {
        icon: "orders",
        testId: "orders"
      },
      link: getLinkAttributes(ordersLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.orders),
        overrideLinkAttributes: position ? {
          href: ordersLinkAttributes?.href ?? "",
          onClick: (e) => (logMyOrdersClickEvent(position), ordersLinkAttributes?.onClick(e))
        } : void 0
      })
    },
    {
      label: intl.getLabel("account.menu.wishlist"),
      parameters: {
        icon: "favourite-empty",
        testId: "wishlist"
      },
      link: getLinkAttributes(wishlistLinkAttributes, {
        isLinkEnabled: isWishlistEnabled && hasRoute(routes?.wishlist)
      })
    },
    {
      label: intl.getLabel("account.menu.subscriptions"),
      parameters: {
        icon: "shipment-next",
        testId: "subscriptions"
      },
      link: getLinkAttributes(subscriptionsLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.subscriptions)
      })
    },
    {
      label: intl.getLabel("account.menu.cccSubscriptions"),
      parameters: {
        icon: "shipment-next",
        testId: "cccSubscriptions"
      },
      link: getLinkAttributes(cccSubscriptionsLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.cccSubscriptions)
      })
    },
    {
      label: intl.getLabel("account.menu.settings"),
      parameters: {
        icon: "folded-page-with-text",
        testId: "settings"
      },
      link: getLinkAttributes(settingsLinkAttributes, {
        isLinkEnabled: hasRoute(routes?.settings)
      })
    },
    lastItem
  ];
  const hasLink = (item) => Boolean(item.link?.href && item.link.href !== "");
  const hasAction = (item) => Boolean(item.parameters?.action);
  return items.filter((item) => hasLink(item) || hasAction(item));
};
export {
  useMyAccountMenuItems
};
//# sourceMappingURL=use-my-account-menu-items.js.map
